import { type NavBadges, type NavBadgesContext, setNavBadgesSource } from '../../lib/nav-badges';
import { agentsNavBadges } from '../agents';
import { approvalsNavBadges } from '../approvals';
import {
  registerClientOverviewSection,
  setClientAddTimeDialog,
  setClientNewTaskDialog,
  setClientTab,
} from '../clients/slots';
import { ClientHistoryTab, ClientRecentNotes } from '../history';
import { inboxNavBadges, setInboxMessageView } from '../inbox';
import { ClientKnowledgeTab, ProjectKnowledgeTab } from '../knowledge';
import { setThreadActions } from '../mail';
import { ClientMeetingsTab } from '../meetings';
import { ClientOffersTab } from '../offers';
import { registerProjectOverviewSection, setProjectsServicesTab, setProjectTab } from '../projects/slots';
import { ServicesListTab } from '../services';
import { NewTaskDialog } from '../tasks';
import { AddTimeDialog } from '../time';
import {
  ClientAiSummary,
  ClientOpenTasks,
  ClientOverviewTop,
  ClientProjectsAndServices,
  ClientServerSection,
} from './client-overview';
import { InboxMessageView, ThreadTaskActions } from './inbox-message';
import {
  ClientMaintenance,
  ClientMessagesTab,
  ClientTasksTab,
  ClientTimeTab,
  ProjectHistoryTab,
  ProjectOpenTasks,
  ProjectTasksTab,
  ProjectTimeTab,
} from './tabs';

/**
 * Kompozycja Fal 1–4: spina moduły w ekrany przekrojowe przez sloty modułów (bez zmian w ich kodzie).
 * Fala 3: plakietki menu (Inbox, Akceptacje), historia klienta = zdarzenia (Inbox) + zmiany (audyt), baner akceptacji
 * w profilu klienta prowadzi do listy akcji klienta w Akceptacjach.
 * Fala 4: wiadomość w Inboxie = wątek poczty z odpowiedzią, Copilotem, przypisaniem i „Utwórz zadanie”
 * (`setInboxMessageView`; akcje wątku `setThreadActions` — także `/poczta/:id` i profil klienta), zakładka
 * „Wiadomości” klienta, „W skrócie” (podsumowanie AI) w Przeglądzie klienta.
 * Fala 5: plakietka „Agenci” (Muted, wyniki AI do odbioru); karta zadania, ⌘K i Ustawienia → Tokeny i agenci składają
 * moduły bezpośrednio (`TaskPage`, `CommandPalette` + `AssignAiHost`, `settings-tokens.tsx`).
 * Ładowane efektem ubocznym importu w `app/routes.tsx` (razem z trasami), więc działa w aplikacji i w E2E.
 *
 * Profil klienta (Figma 05 Profil REA): Przegląd — baner akceptacji i kafelki (główna oferta, do rozliczenia,
 * godziny w miesiącu, najbliższe spotkanie), „Projekty i usługi”, „Na naszym serwerze” (status z monitoringu)
 * i „W skrócie” (lewa), „Otwarte zadania” nad „Kontaktami” (prawa); zakładki Zadania, Czas, Utrzymanie, Wiadomości,
 * Spotkania, Wiedza, Oferty, Historia; „Dodaj czas” w nagłówku otwiera formularz czasu z klientem.
 * Projekt: Przegląd z otwartymi zadaniami nad zespołem; zakładki Zadania, Czas (wykorzystanie budżetu godzin
 * i wpisy), Wiedza, Historia. Lista projektów: zakładka „Usługi” z modułu usług.
 */
let installed = false;

/**
 * Plakietki menu (kompozycja Fali 3, DESIGN §5): Inbox — „Unread” (liczba nieprzeczytanych, pogrubiona), Akceptacje —
 * „Attention” (niebieska, akcje, które osoba może zatwierdzić; ta sama reguła co kafelek „Do akceptacji” w Dziś).
 * Liczniki tylko dla modułów osoby; błąd jednego licznika nie ukrywa drugiego, błąd obu zostawia poprzedni stan.
 */
export async function compositionNavBadges({ client, can }: NavBadgesContext): Promise<NavBadges> {
  const parts = await Promise.allSettled([
    can('inbox') ? inboxNavBadges(() => client.inbox.counts.query()) : Promise.resolve<NavBadges>({}),
    can('approvals') ? approvalsNavBadges(() => client.approvals.counts.query()) : Promise.resolve<NavBadges>({}),
    // Fala 5: Agenci — „Muted”, moje wyniki AI do odbioru
    can('agents') ? agentsNavBadges(() => client.agents.counts.query()) : Promise.resolve<NavBadges>({}),
  ]);
  const ok = parts.filter((p): p is PromiseFulfilledResult<NavBadges> => p.status === 'fulfilled');
  if (ok.length === 0) {
    const first = parts[0];
    if (first?.status === 'rejected') throw first.reason;
  }
  return Object.assign({}, ...ok.map((p) => p.value));
}

export function installComposition(): void {
  if (installed) return;
  installed = true;

  setNavBadgesSource(compositionNavBadges);

  registerClientOverviewSection({ key: 'summary', column: 'top', order: 10, component: ClientOverviewTop });
  registerClientOverviewSection({
    key: 'projects-services',
    column: 'left',
    order: 10,
    component: ClientProjectsAndServices,
  });
  registerClientOverviewSection({
    key: 'maintenance-assets',
    column: 'left',
    order: 20,
    component: ClientServerSection,
  });
  registerClientOverviewSection({ key: 'ai-summary', column: 'left', order: 30, component: ClientAiSummary });
  registerClientOverviewSection({ key: 'open-tasks', column: 'right', order: 10, component: ClientOpenTasks });
  // Fala 10: „Ostatnie notatki” (przypięte + ostatnie, szybkie „Dodaj notatkę…”) nad „Kontaktami”
  registerClientOverviewSection({ key: 'notes', column: 'right', order: 20, component: ClientRecentNotes });

  setClientTab('tasks', ClientTasksTab);
  setClientTab('time', ClientTimeTab);
  setClientTab('maintenance', ClientMaintenance);
  setClientTab('messages', ClientMessagesTab);
  setClientTab('meetings', ClientMeetingsTab);
  setClientTab('knowledge', ClientKnowledgeTab);
  setClientTab('offers', ClientOffersTab);
  // Fala 10: Historia = notatki + jedna oś czasu (zmiany, maile, spotkania, zadania, czas, akceptacje, przebiegi)
  setClientTab('history', ClientHistoryTab);
  setClientNewTaskDialog(({ client, open, onOpenChange }) => (
    <NewTaskDialog open={open} onOpenChange={onOpenChange} defaultClientId={client.id} />
  ));
  setClientAddTimeDialog(({ client, open, onOpenChange }) => (
    <AddTimeDialog open={open} onOpenChange={onOpenChange} defaultClientId={client.id} />
  ));

  registerProjectOverviewSection({ key: 'open-tasks', column: 'right', order: 10, component: ProjectOpenTasks });
  setProjectTab('tasks', ProjectTasksTab);
  setProjectTab('time', ProjectTimeTab);
  setProjectTab('knowledge', ProjectKnowledgeTab);
  setProjectTab('history', ProjectHistoryTab);
  setProjectsServicesTab(ServicesListTab);

  setInboxMessageView(InboxMessageView);
  setThreadActions(ThreadTaskActions);
}

installComposition();
